import React, { useMemo, useEffect } from 'react';
import { useFlexSimulatorLogic } from './useFlexSimulatorLogic';
import { useLayoutContext } from '../../contexts/LayoutContext';
import { TIPO_FLUXO, TIPO_CORRECAO, ALL_PLANOS } from '../../utils/constantes';
import { parseCurrency } from '../../utils/formatters';
import { getPlanoFieldRules, getParcelasLimits } from './utils/planoRules';
import { formatPrazoDeObra, formatCurrency } from '../../utils/formatters';
import AppLayout from '../../components/layout/AppLayout/AppLayout';
import Card from '../../components/layout/Card/Card';
import FormRow from '../../components/form/FormRow/FormRow';
import FormColumn from '../../components/form/FormColumn/FormColumn';
import SelectField from '../../components/form/SelectField/SelectField';
import AutocompleteField from '../../components/form/AutocompleteField/AutocompleteField';
import InputField from '../../components/form/InputField/InputField';
import NumericField from '../../components/form/NumericField/NumericField';
import Button from '../../components/form/Button/Button';
import TextDisplay from '../../components/form/TextDisplay/TextDisplay';
import FlexSimulationResult from './components/FlexSimulationResult/FlexSimulationResult';
import PrintButton from '../../components/print/PrintButton/PrintButton';
import FlexPrint from './components/FlexPrint/FlexPrint';
import styles from './FlexSimulatorPage.module.css';
import ToggleSwitch from '../../components/form/ToggleSwitch/ToggleSwitch';
import AlertContainer from '../../components/layout/Alert/AlertContainer';
import Section from '../../components/layout/Section/Section';
import IntermediateTable from '../../components/shared/IntermediateTable/IntermediateTable';

function FlexSimulatorPage() {
    const { isMRV } = useLayoutContext();
    const {
        formData,
 
        // Dados da API
        loading,
        estados,
        cidades,
        empreendimentos,
        blocos,
        unidades,
        selectedEstado,
        selectedCidade,
        selectedEmpreendimento,
        selectedBloco,
        selectedUnidade,
        selectedUnidadeData,
        isPagadoria,
        isRendaExterior,
        
        // Dados da inicialização
        dadosInicializacao,
        valorFinal,
        valorTabelaState,
        loadingInicializacao,
        loadingSimulacao,
        simulationResult,
        showResult,
        isPrintEnabled,
        loadingUnidades,
        intermediates,
 
        // Handlers
        handleEstadoChange,
        handleCidadeChange,
        handleEmpreendimentoChange,
        handleBlocoChange,
        handleUnidadeChange,
        handlePagadoriaChange,
        handleRendaExteriorChange,
        handleClearEmpreendimento,
        handleClearUnidade,
 
        planosDisponiveis,
        tiposFluxoDisponiveis,
        tiposCorrecaoDisponiveis,
        updatePlano,
        limparFormulario,
        enviarAprovacao,
        handlePrintClick,
        setFormData,
        handleIntermediatesChange,
        handleFgtsChange,
        handleAtopChange,
        handleDescontoChange,
        
        // Signal Distribution Handlers
        handleAtoChange,
        handleCartaDeCreditoChange,
        handleE2Change,
        handleE3Change,
        handleQuantidadeParcelasChange,
        handleQuantidadeParcelasBlur,
        showError
    } = useFlexSimulatorLogic(isMRV);

    // Preencher automaticamente a quantidade de parcelas quando os dados de inicialização forem carregados
    useEffect(() => {
        if (dadosInicializacao?.quantidadeMaximaParcelasMensais) {
            setFormData(prev => ({ 
                ...prev, 
                quantidadeParcelas: dadosInicializacao.quantidadeMaximaParcelasMensais 
            }));
        }
    }, [dadosInicializacao?.quantidadeMaximaParcelasMensais, setFormData]);

    // Calcular regras de habilitação de campos com base no plano selecionado
    const fieldRules = useMemo(() => {
        const planoSelecionado = planosDisponiveis[formData.plano];
        return getPlanoFieldRules(planoSelecionado);
    }, [formData.plano, planosDisponiveis]);

    // Calcular maxLimit de intermediárias com base em quantidadeParcelasPSA
    // Regra Backend: max_value = 5 if quantidadeParcelasPSA > 5 else max(quantidadeParcelasPSA - 1, 0)
    const maxIntermediarias = useMemo(() => {
        const quantidadeParcelasPSA = dadosInicializacao?.quantidadeParcelasPSA || 0;
        return quantidadeParcelasPSA > 5 ? 5 : Math.max(quantidadeParcelasPSA - 1, 0);
    }, [dadosInicializacao?.quantidadeParcelasPSA]);

    // Verificar se campo de intermediárias deve estar desabilitado
    // Regra Backend: disabled = quantidadeParcelasPSA <= 6
    const isIntermediariasDisabled = useMemo(() => {
        const quantidadeParcelasPSA = dadosInicializacao?.quantidadeParcelasPSA || 0;
        return quantidadeParcelasPSA <= 6;
    }, [dadosInicializacao?.quantidadeParcelasPSA]);

    // Verificar se é plano Fixo 12 ou Fixo 18 (para remover limites min/max)
    const isPlanoFixo = useMemo(() => {
        const planoSelecionado = planosDisponiveis[formData.plano];
        return planoSelecionado === ALL_PLANOS.Fixo12 || planoSelecionado === ALL_PLANOS.Fixo18;
    }, [formData.plano, planosDisponiveis]);

    const isPlanoAvista = useMemo(() => {
        const planoSelecionado = planosDisponiveis[formData.plano];
        return planoSelecionado === ALL_PLANOS.AVista;
    }, [formData.plano, planosDisponiveis]);
    
    // Verificar se é plano Financiamento Bancário (para ajustar layout dos campos)
    const isFinanciamentoBancario = useMemo(() => {
        const planoSelecionado = planosDisponiveis[formData.plano];
        return planoSelecionado === ALL_PLANOS.FinanciamentoBancario;
    }, [formData.plano, planosDisponiveis]);

    // Calcular colunas para campos monetários baseado no plano e visibilidade de campos
    const formColumnSize = useMemo(() => {
        if (isFinanciamentoBancario) return 3;
        if (fieldRules.renda?.visible) return 3;
        return 4;
    }, [isFinanciamentoBancario, fieldRules.renda?.visible]);

    // Calcular valor mínimo para sinais do plano à vista (1/3 do valor final com desconto FGTS, ATOP e Desconto)
    const minAVistaSignal = useMemo(() => {
        if (!isPlanoAvista || !valorFinal) return 0;
        const fgtsValue = parseCurrency(formData.fgts) || 0;
        const atopValue = parseCurrency(formData.atop) || 0;
        const descontoValue = parseCurrency(formData.desconto) || 0;
        const totalDescontos = fgtsValue + atopValue + descontoValue;
        const valorFinalEfetivo = valorFinal - totalDescontos;
        return valorFinalEfetivo > 0 ? valorFinalEfetivo / 3 : 0;
    }, [isPlanoAvista, valorFinal, formData.fgts, formData.atop, formData.desconto]);

    // Ato mínimo do plano (mesmo percentual usado no backend em PropostaFlexUtils.
    // CalcularAtoMinimoMrv/CalcularPercentualAtoMinimoMrv). Base para prever o Ato Complementar
    // quando a Carta de Crédito não cobre esse mínimo — ver GeradorPropostaFlexAVista.Simular.
    const atoMinimoPlano = dadosInicializacao?.limites?.ato?.minimo || 0;

    // Ato Complementar previsto: diferença entre o ato mínimo do plano e o valor da Carta de
    // Crédito digitado, quando a Carta não cobre esse mínimo. Só é uma prévia (o valor final vem
    // da Simulação), pois aqui o ato mínimo é calculado na inicialização, sobre o desconto máximo
    // do plano — não sobre o FGTS/ATOP/Desconto que o usuário ainda vai preencher.
    const atoComplementarPrevisto = useMemo(() => {
        if (!formData.cartaDeCredito) return 0;
        const valorCarta = parseCurrency(formData.valorCartaDeCredito) || 0;
        return Math.max(0, atoMinimoPlano - valorCarta);
    }, [formData.cartaDeCredito, formData.valorCartaDeCredito, atoMinimoPlano]);

      const totalMinSignal = useMemo(() => {
        if (!dadosInicializacao?.limites) return 0;
        return (dadosInicializacao.limites.ato?.minimo || 0)
            + (dadosInicializacao.limites.e2?.minimo || 0)
            + (dadosInicializacao.limites.e3?.minimo || 0);
    }, [dadosInicializacao?.limites]);

      const minE2 = useMemo(() => {
        if (isPlanoAvista) return minAVistaSignal;
        const atoValue = parseCurrency(formData.ato) || 0;
        const e3Value = parseCurrency(formData.e3) || 0;
        return Math.max(0, totalMinSignal - atoValue - e3Value);
    }, [isPlanoAvista, minAVistaSignal, formData.ato, formData.e3, totalMinSignal]);

     const minE3 = useMemo(() => {
        if (isPlanoAvista) return minAVistaSignal;
        const atoValue = parseCurrency(formData.ato) || 0;
        const e2Value = parseCurrency(formData.e2) || 0;
        return Math.max(0, totalMinSignal - atoValue - e2Value);
    }, [isPlanoAvista, minAVistaSignal, formData.ato, formData.e2, totalMinSignal]);

    // Preparar dados de propriedade para impressão
    const propertyDataForPrint = useMemo(() => ({
        selectedProperty: selectedEmpreendimento ? 
            empreendimentos.find(e => e.value === selectedEmpreendimento)?.label : '',
        selectedUnit: selectedUnidade ?
            unidades.find(u => u.value === selectedUnidade)?.label : '',
        propertyValue: dadosInicializacao?.valorTabela || 0,
        priceTableVersion: '',
        cidade: selectedCidade ?
            cidades.find(c => c.value === selectedCidade)?.label : '',
        bloco: selectedBloco ?
            blocos.find(b => b.value === selectedBloco)?.label : '',
        planoVenda: formData.plano ?
            planosDisponiveis[formData.plano] : '',
        isRendaExterior: isRendaExterior,
        linhaProduto: empreendimentos.find(e => e.value === selectedEmpreendimento)?.data?.padronizacao?.nome || 'N/A'
    }), [selectedEmpreendimento, empreendimentos, selectedUnidade, unidades, 
        dadosInicializacao?.valorTabela, selectedCidade, cidades, selectedBloco, 
        blocos, formData.plano, planosDisponiveis, isRendaExterior]);

    // Calcular data de prazo de obra
    const prazoDeObraFormatado = useMemo(() => {
        return formatPrazoDeObra(
            selectedUnidadeData?.prosolutoA,
            selectedUnidadeData?.bloco?.acompanhamentoMensalProducao
        );
    }, [selectedUnidadeData]);

    const validarDataInicial = (data) => {
        if(!data){
            return 3;
        }else if(data === "0" || data === 0){
            return 3;
        }else{
            return 4;
        }
    }

    return (
        <>
            <AlertContainer />
            <AppLayout>
                <Card title="Informações do Imóvel">
                                {(loading || loadingUnidades) && (
                                    <div className={styles.loadingOverlay}>
                                        <div className={styles.spinner}></div>
                                        <span>
                                          {loading ? 'Carregando empreendimentos...' : 'Carregando unidades...'}
                                        </span>
                                    </div>
                                )}
                                <FormRow>
                                    <FormColumn cols={3}>
                                        <SelectField
                                            id="estado"
                                            label="Estado"
                                            value={selectedEstado}
                                            onChange={(e) => handleEstadoChange(e.target.value)}
                                            options={estados}
                                            isDisabled={loading}
                                        />
                                    </FormColumn>
                                    <FormColumn cols={3}>
                                        <SelectField
                                            id="cidade"
                                            label="Cidade"
                                            value={selectedCidade}
                                            onChange={(e) => handleCidadeChange(e.target.value)}
                                            options={cidades}
                                            isDisabled={!selectedEstado || loading}
                                        />
                                    </FormColumn>
                                    <FormColumn cols={3}>
                                        <AutocompleteField
                                            id="empreendimento"
                                            label="Empreendimento"
                                            value={selectedEmpreendimento}
                                            onChange={(e) => handleEmpreendimentoChange(e.target.value)}
                                            onClear={handleClearEmpreendimento}
                                            options={empreendimentos}
                                            isDisabled={loading || empreendimentos.length === 0}
                                            placeholder="Digite para buscar..."
                                        />
                                    </FormColumn>
                                    <FormColumn cols={3}>
                                        <SelectField
                                            id="bloco"
                                            label="Bloco"
                                            value={selectedBloco}
                                            onChange={(e) => handleBlocoChange(e.target.value)}
                                            options={blocos}
                                            isDisabled={!selectedEmpreendimento || loading}
                                        />
                                    </FormColumn>
                                </FormRow>

                                <FormRow>
                                    <FormColumn cols={3}>
                                        <AutocompleteField
                                            id="unidade"
                                            label="Unidade"
                                            value={selectedUnidade}
                                            onChange={(e) => handleUnidadeChange(e.target.value)}
                                            onClear={handleClearUnidade}
                                            options={unidades}
                                            isDisabled={!selectedBloco || loading}
                                            placeholder="Digite para buscar..."
                                        />
                                    </FormColumn>
                                    <FormColumn cols={3}>
                                        <ToggleSwitch
                                            style={{ marginTop: 35 }}
                                            id="pagadoria"
                                            label="É Pagadoria?"
                                            isChecked={isPagadoria}
                                            onChange={handlePagadoriaChange}
                                            disabled={Boolean(formData.cartaDeCredito)}
                                        />
                                    </FormColumn>
                                    <FormColumn cols={3}>
                                        <ToggleSwitch
                                            style={{ marginTop: 35 }}
                                            id="renda-exterior"
                                            label="Cliente de Renda Exterior?"
                                            isChecked={isRendaExterior}
                                            onChange={handleRendaExteriorChange}
                                        />
                                    </FormColumn>
                                    <FormColumn cols={3}>
                                        <ToggleSwitch
                                            style={{ marginTop: 35 }}
                                            id="cartaDeCredito"
                                            label="Carta de Crédito"
                                            isChecked={Boolean(formData.cartaDeCredito)}
                                            onChange={(e) => {
                                                const isChecked = e.target.checked;
                                                setFormData(prev => ({
                                                    ...prev,
                                                    cartaDeCredito: isChecked,
                                                    // Ao desmarcar, ato/e2/e3 podem estar desatualizados: enquanto a Carta
                                                    // estava marcada, handleCartaDeCreditoChange os preencheu com base no
                                                    // "ato efetivo" (Math.max(carta, atoMinimo)), não no valor bruto
                                                    // digitado na Carta. Sem resetar, a validação de À Vista (que volta a
                                                    // valer aqui, pois só é pulada com cartaDeCredito=true) compara esses
                                                    // valores obsoletos com o valorFinal e falha de forma confusa para o
                                                    // usuário. valorCartaDeCredito não precisa ser resetado: o campo fica
                                                    // oculto e não entra em nenhuma validação enquanto cartaDeCredito for
                                                    // false.
                                                    ...(!isChecked && { ato: '', e2: '', e3: '' })
                                                }));
                                            }}
                                            disabled={isPagadoria}
                                            tooltip="Não pode ser utilizada em conjunto com Pagadoria. Outras incompatibilidades de fluxo (EmCash, Negociação Especial, empreendimentos com loteadora) só são validadas ao gerar a proposta."
                                        />
                                    </FormColumn>
                                </FormRow>
                            </Card>
 
                            <Card title="Parâmetros do Plano">
                                {loadingInicializacao && (
                                    <div className={styles.loadingOverlay}>
                                        <div className={styles.spinner}></div>
                                        <span>Carregando dados do plano...</span>
                                    </div>
                                )}
                                <FormRow>
                                    <FormColumn cols={8}>
                                        <SelectField
                                            id="plano"
                                            label="Plano de pagamento"
                                            value={formData.plano}
                                            onChange={(e) => updatePlano(e.target.value)}
                                            options={planosDisponiveis.map((plano, index) => ({
                                                value: String(index),
                                                label: plano
                                            }))}
                                            isDisabled={!selectedUnidade || planosDisponiveis.length === 0}
                                        />
                                    </FormColumn>
                                       {fieldRules.eSAD?.visible && (
                                            <FormColumn cols={2}>
                                                <ToggleSwitch
                                                    style={{ marginTop: 35 }}
                                                    id="eSAD"
                                                    label="É SAD?"
                                                    isChecked={formData.eSAD}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, eSAD: e.target.checked }))}
                                                    disabled={fieldRules.eSAD.disabled}
                                                />
                                            </FormColumn>
                                        )}
                                        {formData.eSAD && (
                                            <FormColumn cols={2}>
                                                <ToggleSwitch
                                                    style={{ marginTop: 35 }}
                                                    id="legado"
                                                    label="É Legado?"
                                                    isChecked={formData.legado || false}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, legado: e.target.checked }))}
                                                />
                                              
                                            </FormColumn>
                                        )}
                                </FormRow>
                                <FormRow>
                                     <FormColumn cols={12}>
                                    {formData.eSAD && formData.legado && (
                                                    <div style={{ color: '#d17b00', marginTop: -15, marginBottom: 4, fontWeight: 'bold' }}>
                                                        Exceções de SAD no legado: unidades; PNE, CSG, Carta de Crédito, Casas, Estrangeiro, PJ, Separação total de bens, Casado sem alteração de nome na receita.
                                                    </div>
                                                )}
                                    </FormColumn>
                                    </FormRow>
                                <FormRow>
                                    <FormColumn cols={4}>
                                        <SelectField
                                            id="tipo-fluxo"
                                            label="Selecione o Tipo de Fluxo"
                                            value={formData.tipoFluxo}
                                            onChange={(e) => setFormData(prev => ({ ...prev, tipoFluxo: e.target.value }))}
                                            options={tiposFluxoDisponiveis.map(key => ({
                                                value: key,
                                                label: TIPO_FLUXO[key]
                                            }))}
                                            isDisabled={fieldRules.tipoFluxo.disabled || tiposFluxoDisponiveis.length === 0}
                                        />
                                    </FormColumn>
                                    <FormColumn cols={4}>
                                        <InputField
                                            id="parcelas"
                                            label="Quantidade de Parcelas Mensais"
                                            value={formData.quantidadeParcelas}
                                            onChange={(e) => handleQuantidadeParcelasChange(e.target.value)}
                                            onBlur={(e) =>  handleQuantidadeParcelasBlur(e.target.value)}
                                            type='number'
                                            min={isPlanoFixo ? 3 : getParcelasLimits(planosDisponiveis[formData.plano], dadosInicializacao?.quantidadeParcelasPSA, formData.eSAD, dadosInicializacao?.quantidadeMaximaParcelasMensais).min}
                                            max={getParcelasLimits(planosDisponiveis[formData.plano], dadosInicializacao?.quantidadeParcelasPSA, formData.eSAD, dadosInicializacao?.quantidadeMaximaParcelasMensais).max || dadosInicializacao?.quantidadeMaximaParcelasMensais} 
                                            
                                            tooltip={!fieldRules.quantidadeParcelas.disabled ? `O valor deve estar entre ${isPlanoFixo ? 3 : getParcelasLimits(planosDisponiveis[formData.plano], dadosInicializacao?.quantidadeParcelasPSA, formData.eSAD, dadosInicializacao?.quantidadeMaximaParcelasMensais).min} 
                                            e ${getParcelasLimits(planosDisponiveis[formData.plano], dadosInicializacao?.quantidadeParcelasPSA, formData.eSAD, dadosInicializacao?.quantidadeMaximaParcelasMensais).max || dadosInicializacao?.quantidadeMaximaParcelasMensais}` : ""}
                                            disabled={fieldRules.quantidadeParcelas.disabled}
                                        />
                                    </FormColumn>
                                    <FormColumn cols={4}>
                                        <SelectField
                                        id="tipo-correcao"
                                        label="Selecione o Tipo de Correção"
                                        value={formData.tipoCorrecao}
                                        onChange={(e) => setFormData(prev => ({ ...prev, tipoCorrecao: e.target.value }))}
                                        options={tiposCorrecaoDisponiveis.map(key => ({
                                            value: key,
                                            label: TIPO_CORRECAO[key]
                                        }))}
                                        isDisabled={fieldRules.tipoCorrecao.disabled || tiposCorrecaoDisponiveis.length === 0}
                                        />
                                    </FormColumn>
                                </FormRow>
 
                                <FormRow>
                                    {!formData.cartaDeCredito && (
                                        <FormColumn cols={4}>
                                            <NumericField
                                                id="ato"
                                                label="Ato (1ª Parcela)"
                                                value={formData.ato}
                                                onChange={(e) => handleAtoChange(e.target.value)}
                                                placeholder="R$ 0,00"
                                                currency={true}
                                                min={isPlanoAvista ? minAVistaSignal : dadosInicializacao?.limites?.ato?.minimo}
                                                max={dadosInicializacao?.limites?.ato?.maximo}
                                                disabled={fieldRules.ato.disabled}
                                                tooltip={formData.plano && dadosInicializacao?.limites?.ato?.minimo ? `O valor deve estar entre ${isPlanoAvista ? formatCurrency(minAVistaSignal) : formatCurrency(dadosInicializacao?.limites?.ato?.minimo)} 
                                            e ${formatCurrency(dadosInicializacao?.limites?.ato?.maximo)}` : ""}
                                            />
                                        </FormColumn>
                                    )}
                                    {formData.cartaDeCredito && (
                                        <FormColumn cols={4}>
                                            <NumericField
                                                id="valorCartaDeCredito"
                                                label="Valor da Carta de Crédito"
                                                value={formData.valorCartaDeCredito}
                                                onChange={(e) => {
                                                    // O valor da carta em si só muda por digitação direta (imutável às
                                                    // trocas de plano, parcelas etc. — ver INITIAL_FORM_DATA/handlePlanoChange).
                                                    // Ainda assim, no plano À Vista precisamos redistribuir E2/E3 com base
                                                    // nesse valor, senão E2/E3 ficam desatualizados e a simulação some com
                                                    // o valor errado. handleCartaDeCreditoChange (diferente do handleAtoChange
                                                    // usado pelo campo Ato normal) considera o ato mínimo do plano nessa
                                                    // redistribuição — replicando a regra do backend (GeradorPropostaFlexAVista.
                                                    // Simular) de que a diferença entre a Carta e o ato mínimo vira Ato
                                                    // Complementar, não é absorvida por E2/E3. Também grava em formData.ato,
                                                    // mas esse campo fica oculto ao usuário enquanto a Carta estiver marcada,
                                                    // então isso é inofensivo.
                                                    setFormData(prev => ({ ...prev, valorCartaDeCredito: e.target.value }));
                                                    handleCartaDeCreditoChange(e.target.value, atoMinimoPlano);
                                                }}
                                                placeholder="R$ 0,00"
                                                currency={true}
                                                // Sem min: diferente do Ato, um valor de Carta abaixo do ato mínimo do
                                                // plano é um cenário válido e esperado (não um erro) — é exatamente o
                                                // que aciona o Ato Complementar (ver atoComplementarPrevisto acima e
                                                // handleCartaDeCreditoChange). Aplicar o mesmo min do Ato aqui bloquearia
                                                // essa combinação inteira na UI.
                                                max={dadosInicializacao?.limites?.ato?.maximo}
                                                disabled={fieldRules.ato.disabled}
                                                tooltip="Valor fixo da carta de crédito. Diferente do Ato, este valor não é recalculado automaticamente pelo simulador (troca de plano, quantidade de parcelas etc.) — só muda se você digitar um novo valor. Se não cobrir o ato mínimo do plano, a diferença aparece ao lado como Ato Complementar (previsto)."
                                            />
                                        </FormColumn>
                                    )}
                                    {atoComplementarPrevisto > 0 && (
                                        <FormColumn cols={4}>
                                            <NumericField
                                                id="atoComplementarPrevisto"
                                                label="Ato Complementar (previsto)"
                                                value={atoComplementarPrevisto}
                                                onChange={() => {}}
                                                placeholder="R$ 0,00"
                                                currency={true}
                                                disabled={true}
                                                tooltip="Quando a Carta de Crédito não cobre o ato mínimo do plano, um Ato Complementar é adicionado. A diferença deve ser paga como Ato Complementar, com vencimento D+1. O valor exibido aqui é previsto — a Simulação confirma o valor final."
                                            />
                                        </FormColumn>
                                    )}
                                    <FormColumn cols={4}>
                                        <NumericField
                                            id="e2"
                                            label="E2 (2ª Parcela)"
                                            value={formData.e2}
                                            onChange={(e) => handleE2Change(e.target.value, formData.ato)}
                                            placeholder="R$ 0,00"
                                            currency={true}
                                            min={isPlanoAvista ? minAVistaSignal : minE2}
                                            max={isPlanoAvista ? undefined : dadosInicializacao?.limites?.e2?.maximo}
                                            disabled={fieldRules.e2.disabled}
                                            tooltip={formData.plano && totalMinSignal > 0 ? `Sinal mínimo total (Ato+E2+E3): ${formatCurrency(totalMinSignal)}. Mínimo atual: ${formatCurrency(minE2)}` : ""}
                                        />
                                    </FormColumn>
                                    <FormColumn cols={4}>
                                        <NumericField
                                            id="e3"
                                            label="E3 (3ª Parcela)"
                                            value={formData.e3}
                                            onChange={(e) => handleE3Change(e.target.value, formData.ato)}
                                            placeholder="R$ 0,00"
                                            currency={true}
                                            min={isPlanoAvista ? 0 : minE3}
                                            max={isPlanoAvista ? undefined : dadosInicializacao?.limites?.e3?.maximo}
                                            disabled={fieldRules.e3.disabled}
                                            tooltip={formData.plano && totalMinSignal > 0 ? `Sinal mínimo total (Ato+E2+E3): ${formatCurrency(totalMinSignal)}. Mínimo atual: ${formatCurrency(minE3)}` : ""}
                                        />
                                    </FormColumn>
                                </FormRow>
                                <FormRow>
                                    <FormColumn cols={formColumnSize}>
                                        <NumericField
                                            id="aumento"
                                            label="Aumento"
                                            value={formData.aumento}
                                            onChange={(e) => setFormData(prev => ({ ...prev, aumento: e.target.value }))}
                                            placeholder="R$ 0,00"
                                            currency={true}
                                            disabled={fieldRules.aumento.disabled}
                                        />
                                    </FormColumn>
                                    <FormColumn cols={formColumnSize}>
                                        <NumericField
                                            id="desconto"
                                            label="Desconto"
                                            value={formData.desconto}
                                            onChange={(e) => handleDescontoChange(e.target.value)}
                                            placeholder="R$ 0,00"
                                            currency={true}
                                            disabled={fieldRules.desconto.disabled}
                                        />
                                    </FormColumn>
                                    {fieldRules.fgts?.visible && (
                                        <FormColumn cols={formColumnSize}>
                                            <NumericField
                                                id="fgts"
                                                label="FGTS"
                                                value={formData.fgts}
                                                onChange={(e) => handleFgtsChange(e.target.value)}
                                                placeholder="R$ 0,00"
                                                currency={true}
                                                disabled={fieldRules.fgts.disabled}
                                            />
                                        </FormColumn>
                                    )}
                                    {fieldRules.renda?.visible && (
                                        <FormColumn cols={3}>
                                            <NumericField
                                                id="renda"
                                                label="Renda"
                                                value={formData.renda}
                                                onChange={(e) => setFormData(prev => ({ ...prev, renda: e.target.value }))}
                                                placeholder="R$ 0,00"
                                                currency={true}
                                                disabled={fieldRules.renda.disabled}
                                            />
                                        </FormColumn>
                                    )}
                                    {fieldRules.carteira?.visible && (
                                        <FormColumn cols={3}>
                                            <NumericField
                                                id="carteira"
                                                label="Carteira"
                                                value={formData.carteira}
                                                onChange={(e) => setFormData(prev => ({ ...prev, carteira: e.target.value }))}
                                                placeholder="R$ 0,00"
                                                currency={true}
                                                disabled={fieldRules.carteira.disabled}
                                            />
                                        </FormColumn>
                                    )}
                                       {(fieldRules.eSAD?.visible || fieldRules.padraoPsa?.visible) && (
                                            <FormColumn cols={3}>
                                                <NumericField
                                                    id="padraoPsa"
                                                    label="Padrão PSA"
                                                    value={formData.padraoPsa}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, padraoPsa: e.target.value }))}
                                                    placeholder="R$ 0,00"
                                                    currency={true}
                                                    disabled={true}
                                                />
                                            </FormColumn>
                                    )}
                                </FormRow>
                                  

                                {fieldRules.intermediarias?.visible && (
                                    <FormRow>
                                        <FormColumn cols={12}>
                                            <Section title="Parcelas Intermediárias" className="mt-4">
                                                <IntermediateTable
                                                    onIntermediatesChange={handleIntermediatesChange}
                                                    initialIntermediates={intermediates}
                                                    maxLimit={maxIntermediarias}
                                                    replicateValue={false}
                                                    disableValuesExceptFirst={false}
                                                    initialMonthOffset={formData.eSAD ? 4 : validarDataInicial(formData.e3)}
                                                    isDisabled={isIntermediariasDisabled}
                                                    isFlex={true}
                                                    renda={parseCurrency(formData.renda) || 0}
                                                    valorImovel={dadosInicializacao?.valorTabela || 0}
                                                    planoSelecionado={planosDisponiveis[formData.plano]}
                                                    eSAD={formData.eSAD || false}
                                                    onAlertaValorMaximo={showError}
                                                    prosolutoA={selectedUnidadeData?.prosolutoA}
                                                    onAlertaDataMaxima={showError}
                                                    quantidadeParcelasPSA={dadosInicializacao?.quantidadeParcelasPSA || 0}
                                                />
                                            </Section>
                                        </FormColumn>
                                    </FormRow>
                                )}
                                <FormRow>
                                    <FormColumn cols={3}>
                                        <TextDisplay
                                            id="valorTabela"
                                            label="Valor de Tabela"
                                            value={valorTabelaState ? valorTabelaState.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : "R$ 0,00"}
                                        />
                                    </FormColumn>
                                    <FormColumn cols={3}>
                                        <TextDisplay
                                            id="BPpagadoria"
                                            label="BP Pagadoria"
                                            value={dadosInicializacao?.valorBonus ? dadosInicializacao.valorBonus.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : "R$ 0,00"}
                                        />
                                    </FormColumn>
                                    <FormColumn cols={3}>
                                        <TextDisplay
                                            id="valorFinal"
                                            label="Valor Final"
                                            value={valorFinal ? (valorFinal - parseCurrency(formData.fgts) - parseCurrency(formData.desconto)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : "R$ 0,00"}
                                        />
                                    </FormColumn>
                                       <FormColumn cols={3}>
                                        <TextDisplay
                                            id="PrazoDeObra"
                                            label="Prazo de Obra"
                                            value={prazoDeObraFormatado}
                                        />
                                    </FormColumn>
                                </FormRow>
                            </Card>
 
                        <Button
                            id="btnSimular"
                            variant="primary"
                            fullWidth
                            className="mt-4"
                            onClick={enviarAprovacao}
                            disabled={loadingSimulacao}
                        >
                            {loadingSimulacao ? 'Processando...' : 'Simular Proposta'}
                        </Button>

                        {showResult && simulationResult && (
                            <FlexSimulationResult 
                                result={simulationResult}
                                pagadoria={isPagadoria}
                            />
                        )}

                        <div className={styles.ButtonContainer}>
                            <Button onClick={limparFormulario} variant="danger" className={styles.clearButton}>
                                Limpar Formulário
                            </Button>

                            <PrintButton
                                onClick={() => handlePrintClick(propertyDataForPrint.selectedUnit)}
                                disabled={!isPrintEnabled}
                            />
                        </div>

                    <FlexPrint
                        simulationResult={simulationResult}
                        propertyData={propertyDataForPrint}
                        formData={formData}
                        isMRV={isMRV}
                        pagadoria={isPagadoria}
                    />
            </AppLayout>
        </>
    );
}

export default FlexSimulatorPage; 